import { graphQlClient } from '@config/graphqlClient';
import { reportGraphQLErrors, markErrorReported } from '@config/errorReporting';
import {
  getOrganizationId,
  getTokenLocalStorage,
  getTokenSessionStorage,
} from '@config/storage';
import { RootState, store } from '@redux/store';
import FieldCaptureGraphQL from '@graphql/fieldCapture.queries';
import {
  FieldCaptureBackfill,
  FieldCaptureBatchKind,
  FieldCapturePullScope,
  FieldCaptureExport,
  FieldCaptureIngestBatch,
  FieldCaptureOcrRun,
  FieldCaptureOpenVisit,
  FieldCaptureSerialReview,
  FieldCaptureStore,
  FieldCaptureSubmission,
  FieldCaptureValueSource,
} from '@models/fieldCapture.model';

/**
 * Single-file GraphQL multipart upload (graphql-upload spec). The shared
 * graphQlMultipleUpload helper is hardwired to a `files` ARRAY variable, and
 * uploadFieldCapturePull takes a single `input.file` — hence this local twin.
 */
async function uploadSingleFile<T>(
  query: string,
  file: File,
  extraInput: Record<string, unknown>,
): Promise<T> {
  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
  const organizationId = getOrganizationId();

  const operations = {
    query,
    variables: { input: { ...extraInput, file: null } },
  };
  const map = { '0': ['variables.input.file'] };

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));
  formData.append('map', JSON.stringify(map));
  formData.append('0', file);

  const headers: Record<string, string> = {
    'apollo-require-preflight': 'true',
    Authorization: 'Bearer ' + token,
  };
  if (organizationId) {
    headers['X-Organization-ID'] = organizationId;
  }

  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'graphql', {
    method: 'POST',
    body: formData,
    headers,
  });
  const responseData = await response.json();
  if (responseData.errors?.length) {
    reportGraphQLErrors(responseData, { source: 'upload' });
    // A fresh Error, not the object just reported above — mark it explicitly so the
    // QueryCache/MutationCache `onError` handler (`reportCacheError`) recognises this
    // as the same failure and doesn't report it a second time.
    const error = new Error(responseData.errors[0]?.message || 'Upload failed');
    markErrorReported(error);
    throw error;
  }
  if (!response.ok) {
    reportGraphQLErrors({ response: { status: response.status } }, { source: 'upload' });
    // A fresh Error, not the object just reported above — mark it explicitly so the
    // QueryCache/MutationCache `onError` handler (`reportCacheError`) recognises this
    // as the same failure and doesn't report it a second time.
    const error = new Error(`Upload failed (HTTP ${response.status})`);
    markErrorReported(error);
    throw error;
  }
  return responseData.data as T;
}

export const fieldCaptureService = {
  async getBatches(organizationId?: string): Promise<FieldCaptureIngestBatch[]> {
    const data: { fieldCaptureIngestBatches: FieldCaptureIngestBatch[] } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureIngestBatches,
        { organizationId },
      );
    return data.fieldCaptureIngestBatches || [];
  },

  async getLinkTable(search?: string, organizationId?: string): Promise<FieldCaptureStore[]> {
    const data: { fieldCaptureLinkTable: FieldCaptureStore[] } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureLinkTable,
        { search, organizationId },
      );
    return data.fieldCaptureLinkTable || [];
  },

  async getProjects(organizationId?: string): Promise<{ projectId: string; name: string }[]> {
    const data: { fieldCaptureProjects: { projectId: string; name: string }[] } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureProjects,
        { organizationId },
      );
    return data.fieldCaptureProjects || [];
  },

  async getEnabled(): Promise<boolean> {
    const data: { fieldCaptureEnabled: boolean } = await graphQlClient.request(
      FieldCaptureGraphQL.queries.fieldCaptureEnabled,
    );
    return Boolean(data.fieldCaptureEnabled);
  },

  async getQuarantine(): Promise<FieldCaptureSubmission[]> {
    const data: { fieldCaptureQuarantine: FieldCaptureSubmission[] } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureQuarantine,
      );
    return data.fieldCaptureQuarantine || [];
  },

  async uploadPull(
    file: File,
    kind: FieldCaptureBatchKind,
    sourcePullDate?: string,
    organizationId?: string,
    pullScope: FieldCapturePullScope = 'PARTIAL',
  ): Promise<FieldCaptureIngestBatch> {
    const data = await uploadSingleFile<{
      uploadFieldCapturePull: FieldCaptureIngestBatch;
    }>(FieldCaptureGraphQL.mutations.uploadFieldCapturePull, file, {
      kind,
      pullScope,
      ...(sourcePullDate ? { sourcePullDate } : {}),
      ...(organizationId ? { organizationId } : {}),
    });
    return data.uploadFieldCapturePull;
  },

  async applyBatch(batchId: string, organizationId?: string): Promise<FieldCaptureIngestBatch> {
    const data: { applyFieldCaptureIngestBatch: FieldCaptureIngestBatch } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.applyFieldCaptureIngestBatch,
        { batchId, organizationId },
      );
    return data.applyFieldCaptureIngestBatch;
  },

  async discardBatch(batchId: string, organizationId?: string): Promise<FieldCaptureIngestBatch> {
    const data: { discardFieldCaptureIngestBatch: FieldCaptureIngestBatch } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.discardFieldCaptureIngestBatch,
        { batchId, organizationId },
      );
    return data.discardFieldCaptureIngestBatch;
  },

  async linkStoreToProject(
    externalKey: string,
    projectId: string | null,
    organizationId?: string,
  ): Promise<FieldCaptureStore> {
    const data: { linkFieldCaptureStoreToProject: FieldCaptureStore } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.linkFieldCaptureStoreToProject,
        { externalKey, projectId, organizationId },
      );
    return data.linkFieldCaptureStoreToProject;
  },

  async getBrightstarExport(
    organizationId?: string,
    completedOnly?: boolean,
    from?: string,
    to?: string,
  ): Promise<FieldCaptureExport> {
    const data: { fieldCaptureBrightstarExport: FieldCaptureExport } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureBrightstarExport,
        {
          organizationId,
          completedOnly: Boolean(completedOnly),
          // Undefined, not '', so an empty picker means "no window" server-side.
          from: from || undefined,
          to: to || undefined,
        },
      );
    return data.fieldCaptureBrightstarExport;
  },

  async getOpenVisits(organizationId?: string): Promise<FieldCaptureOpenVisit[]> {
    const data: { fieldCaptureOpenVisits: FieldCaptureOpenVisit[] } = await graphQlClient.request(
      FieldCaptureGraphQL.queries.fieldCaptureOpenVisits,
      { organizationId },
    );
    return data.fieldCaptureOpenVisits || [];
  },

  async regenerateLinks(organizationId?: string): Promise<number> {
    const data: { regenerateFieldCaptureLinks: number } = await graphQlClient.request(
      FieldCaptureGraphQL.mutations.regenerateFieldCaptureLinks,
      { organizationId },
    );
    return data.regenerateFieldCaptureLinks ?? 0;
  },

  async getSerialReview(organizationId?: string): Promise<FieldCaptureSerialReview[]> {
    const data: { fieldCaptureSerialReview: FieldCaptureSerialReview[] } =
      await graphQlClient.request(
        FieldCaptureGraphQL.queries.fieldCaptureSerialReview,
        { organizationId },
      );
    return data.fieldCaptureSerialReview || [];
  },

  async runOcr(organizationId?: string, limit?: number): Promise<FieldCaptureOcrRun> {
    const data: { runFieldCaptureOcr: FieldCaptureOcrRun } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.runFieldCaptureOcr,
        { organizationId, limit },
      );
    return data.runFieldCaptureOcr;
  },

  async resolveSerial(
    fieldCaptureValueId: string,
    value: string,
    source: FieldCaptureValueSource,
    organizationId?: string,
  ): Promise<boolean> {
    const data: { resolveFieldCaptureSerial: boolean } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.resolveFieldCaptureSerial,
        { fieldCaptureValueId, value, source, organizationId },
      );
    return data.resolveFieldCaptureSerial;
  },

  async backfillValues(
    organizationId?: string,
    dryRun: boolean = true,
  ): Promise<FieldCaptureBackfill> {
    const data: { backfillFieldCaptureValues: FieldCaptureBackfill } =
      await graphQlClient.request(
        FieldCaptureGraphQL.mutations.backfillFieldCaptureValues,
        { organizationId, dryRun },
      );
    return data.backfillFieldCaptureValues;
  },

  async resolveQuarantined(
    submissionId: string,
    externalKey: string,
    organizationId?: string,
  ): Promise<FieldCaptureSubmission> {
    const data: {
      resolveQuarantinedFieldCaptureSubmission: FieldCaptureSubmission;
    } = await graphQlClient.request(
      FieldCaptureGraphQL.mutations.resolveQuarantinedFieldCaptureSubmission,
      { submissionId, externalKey, organizationId },
    );
    return data.resolveQuarantinedFieldCaptureSubmission;
  },
};

export default fieldCaptureService;
